'use client';

import clsx from 'clsx';
import { highlight, languages } from 'prismjs';
import React, { useEffect,useState } from 'react';
import Editor from 'react-simple-code-editor';
import { toast } from 'react-toastify';
import { ZodIssue } from 'zod';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';

import { Button } from '@/components/button/button';
import Modal from '@/components/modal/modal';
import { useSourcePreview, useUpdateSourceVersion } from '@/hooks/queries/useSources';
import { Source, SourceUpdateReq } from '@/types/evals';

import { jsonConfigSchema } from '../_modalSourceCreate/schemas';
import styles from './ModalSourceEdit.module.scss';

type ModalSourceEditProps = {
  source: Source;
  onClose: () => void;
  onSuccess: ( newVersion?: number ) => void;
};

type Example = {
  id?: string;
  type: 'message' | 'conversation';
  data: Array<{ user: string; message: string }>;
  meta_data?: Record<string, unknown>;
};

export const ModalSourceEdit: React.FC<ModalSourceEditProps> = ( {
  source,
  onClose,
  onSuccess
} ) => {
  const [ jsonInput, setJsonInput ] = useState( '' );
  const [ jsonError, setJsonError ] = useState<string>();
  const [ validationErrors, setValidationErrors ] = useState<string[]>( [] );
  const [ examples, setExamples ] = useState<Example[]>( [] );

  // Fetch source data (up to 100 examples)
  const { data: preview, isLoading, error } = useSourcePreview( source.id, 100 );

  // Mutation for updating source
  const updateMutation = useUpdateSourceVersion();

  const totalCount = preview?.totalCount ?? 0;
  const showWarning = totalCount > 100;
  const isPending = updateMutation.isPending;

  // Transform preview data back to the original format
  const transformPreviewToOriginal = ( item: any ): Example => {
    // Preview data comes flattened, need to convert back to array format
    if ( item.type === 'message' && item.data.input_msg !== undefined ) {
      // Flattened format -> nested array format
      const messages = [];
      messages.push( {
        user: item.data.input_role || 'user',
        message: item.data.input_msg
      } );
      messages.push( {
        user: item.data.output_role || 'assistant',
        message: item.data.output_msg
      } );

      return {
        type: item.type,
        data: messages,
        meta_data: item.data.metadata || item.meta_data
      };
    }

    // If data is already in the correct format, return as-is
    return {
      type: item.type,
      data: item.data,
      meta_data: item.meta_data
    };
  };

  // Initialize editor with fetched data
  useEffect( () => {
    if ( preview?.items ) {
      const fetchedExamples: Example[] = preview.items.map( transformPreviewToOriginal );

      setExamples( fetchedExamples );
      setJsonInput( JSON.stringify( { examples: fetchedExamples }, null, 2 ) );
    }
  }, [ preview ] );

  // Handle ESC key
  useEffect( () => {
    const handleKeyDown = ( event: KeyboardEvent ) => {
      if ( event.key === 'Escape' && !isPending ) {
        onClose();
      }
    };

    window.addEventListener( 'keydown', handleKeyDown );
    return () => window.removeEventListener( 'keydown', handleKeyDown );
  }, [ onClose, isPending ] );

  // Show error toast if fetch fails
  useEffect( () => {
    if ( error ) {
      toast.error( 'Failed to load source data' );
    }
  }, [ error ] );

  const handleJsonInputChange = ( value: string ) => {
    setJsonInput( value );
    setJsonError( undefined );
    setValidationErrors( [] );

    if ( !value.trim() ) {
      setExamples( [] );
      return;
    }

    try {
      const parsed = JSON.parse( value );

      // Validate using the same schema as source creation
      const result = jsonConfigSchema.safeParse( parsed );

      if ( !result.success ) {
        const errors = result.error.issues.map( ( err: ZodIssue ) => {
          const path = err.path.join( '.' );
          return path ? `${ path }: ${ err.message }` : err.message;
        } );
        setValidationErrors( errors );
        return;
      }

      setExamples( result.data.examples );
    } catch( e ) {
      setJsonError( e instanceof Error ? e.message : 'Invalid JSON format' );
    }
  };

  const handleSave = () => {
    if ( jsonError || validationErrors.length > 0 || examples.length === 0 ) {
      toast.error( 'Please fix validation errors before saving' );
      return;
    }

    updateMutation.mutate(
      {
        sourceId: source.id,
        data: {
          examples
        } as unknown as SourceUpdateReq
      },
      {
        onSuccess: ( data ) => {
          toast.success( 'Source version created successfully' );
          onSuccess( data?.version );
          onClose();
        },
        onError: ( error ) => {
          console.error( 'Failed to save source:', error );
          toast.error( 'Failed to save source: ' + ( error instanceof Error ? error.message : 'Unknown error' ) );
        }
      }
    );
  };

  const hasErrors = !!jsonError || validationErrors.length > 0;

  return (
    <Modal>
      <div className={ styles.modal_wrapper }>
        <div className={ styles.modal_header }>
          <h2>Edit JSON Source</h2>
          <p className={ styles.source_name }>{source.name} (Version {source.version})</p>
        </div>

        <div className={ styles.modal_content }>
          {isLoading ? (
            <div className={ styles.loading }>Loading source data...</div>
          ) : error ? (
            <div className={ styles.error_state }>Failed to load source data. Please try again.</div>
          ) : (
            <>
              {showWarning && (
                <div className={ styles.warning }>
                  <strong>Warning:</strong> This source has {totalCount} examples, but only the first 100 can be edited here.
                  The new version will contain only the examples shown below.
                </div>
              )}

              <div className={ styles.editor_section }>
                <label className={ styles.label }>
                  Examples JSON
                  <span className={ styles.count }>
                    ({examples.length} example{examples.length !== 1 ? 's' : ''})
                  </span>
                </label>
                <div
                  className={ clsx(
                    styles.editor_wrapper,
                    hasErrors && styles.error
                  ) }
                >
                  <Editor
                    value={ jsonInput }
                    onValueChange={ handleJsonInputChange }
                    highlight={ ( code ) => highlight( code, languages.json, 'json' ) }
                    padding={ 10 }
                    disabled={ isPending }
                    style={ {
                      fontFamily: '"Fira code", "Fira Mono", monospace',
                      fontSize: 13,
                      minHeight: '500px',
                      maxHeight: '600px',
                      overflow: 'auto'
                    } }
                  />
                </div>
                {jsonError && (
                  <div className={ styles.error_message }>{jsonError}</div>
                )}
                {validationErrors.length > 0 && (
                  <div className={ styles.validation_errors }>
                    {validationErrors.map( ( err, idx ) => (
                      <div key={ idx } className={ styles.error_message }>{err}</div>
                    ) )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className={ styles.modal_footer }>
          <Button
            variant="secondary"
            size="sm"
            onClick={ onClose }
            disabled={ isPending }
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={ handleSave }
            disabled={ isPending || isLoading || hasErrors || examples.length === 0 }
            loading={ isPending }
          >
            Create New Version
          </Button>
        </div>
      </div>
    </Modal>
  );
};